import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  value: string | null | undefined;
  onSave: (next: string) => Promise<unknown> | void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
  inputClassName?: string;
  /** When true, renders even an empty value as a clickable affordance instead of placeholder text. */
  required?: boolean;
}

/**
 * Click-to-edit text field. Click the value to enter edit mode.
 * Save on blur or Enter (Cmd+Enter for multiline). Escape to cancel.
 * Loading state while the onSave promise is in flight.
 */
export function InlineEdit({
  value,
  onSave,
  placeholder = "Click to add",
  multiline = false,
  className,
  inputClassName,
  required = false,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus();
      ref.current.select();
    }
  }, [editing]);

  async function save() {
    if (saving) return;
    const trimmed = draft.trim();
    if (trimmed === (value ?? "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await Promise.resolve(onSave(trimmed));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setDraft(value ?? "");
    setEditing(false);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else if (e.key === "Enter") {
      if (multiline && !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      void save();
    }
  }

  if (editing) {
    const InputEl = multiline ? "textarea" : "input";
    return (
      <InputEl
        ref={ref as React.Ref<HTMLInputElement & HTMLTextAreaElement>}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onBlur={() => void save()}
        onKeyDown={onKey}
        rows={multiline ? Math.max(3, draft.split("\n").length + 1) : undefined}
        className={cn(
          "w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
          "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:opacity-60 disabled:cursor-wait",
          multiline ? "min-h-[80px] resize-y" : "h-10",
          inputClassName,
          className,
        )}
      />
    );
  }

  const isEmpty = !value || value.trim() === "";

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn(
        "group relative w-full text-left rounded-md px-3 py-2 text-sm transition-colors",
        "hover:bg-muted/50 hover:ring-1 hover:ring-border",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isEmpty && !required && "text-muted-foreground",
        multiline && "whitespace-pre-wrap",
        className,
      )}
    >
      <span className="block pr-6 break-words">
        {isEmpty ? placeholder : value}
      </span>
      <Pencil
        size={12}
        className="absolute right-3 top-3 opacity-0 group-hover:opacity-60 transition-opacity text-muted-foreground"
        aria-hidden
      />
    </button>
  );
}
